import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Row, Col, Space, Typography, Switch, Tag, Alert, Input, message, Tooltip, Modal,
} from 'antd';
import { ReloadOutlined, RobotOutlined, EyeOutlined, PoweroffOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');
const { Title, Text, Paragraph } = Typography;

const SEV_COLORS = { alta: 'red', media: 'orange', baixa: 'blue', sem_acao: 'default', error: 'purple' };

const AiSupport = () => {
  const [settings, setSettings] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inboxesInput, setInboxesInput] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get('/webhooks/chatwoot/settings'),
        api.get('/webhooks/chatwoot/suggestions', { params: { limit: 100 } }),
      ]);
      setSettings(s.data);
      setInboxesInput((s.data.allowed_inbox_ids || []).join(','));
      setSuggestions(l.data.suggestions || []);
    } catch (err) {
      message.error('Erro ao carregar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (val) => {
    setSaving(true);
    try {
      const r = await api.patch('/webhooks/chatwoot/settings', { enabled: val });
      setSettings(r.data);
      message.success(val ? 'AI suporte ATIVADO' : 'AI suporte DESATIVADO — nenhum token será gasto');
    } catch (err) {
      message.error('Erro ao salvar');
    } finally { setSaving(false); }
  };

  const saveInboxes = async () => {
    const ids = inboxesInput.split(',').map(s => parseInt(s.trim(), 10)).filter(Number.isFinite);
    setSaving(true);
    try {
      const r = await api.patch('/webhooks/chatwoot/settings', { allowed_inbox_ids: ids });
      setSettings(r.data);
      message.success(`Inboxes permitidas: ${ids.join(', ') || '(nenhuma)'}`);
    } catch (err) {
      message.error('Erro ao salvar');
    } finally { setSaving(false); }
  };

  const columns = [
    { title: 'Quando', dataIndex: 'created_at', width: 160,
      render: (v) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm:ss')}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(v).fromNow()}</Text>
        </Space>
      ),
    },
    { title: 'Severidade', dataIndex: 'severity', width: 100,
      render: (v) => <Tag color={SEV_COLORS[v] || 'default'}>{(v || '').toUpperCase()}</Tag>,
      filters: Object.keys(SEV_COLORS).map(v => ({ text: v, value: v })),
      onFilter: (val, r) => r.severity === val,
    },
    { title: 'Time', dataIndex: 'customer_id', width: 200,
      render: (v, r) => v
        ? <a href={`/customers/${v}/ficha`} target="_blank" rel="noreferrer">#{v}</a>
        : <Text type="secondary" style={{ fontSize: 11 }}>tel: {r.organizer_phone || '—'}</Text>,
    },
    { title: 'Msg do organizador', dataIndex: 'organizer_message',
      render: (v) => <Text ellipsis={{ tooltip: v }} style={{ fontSize: 12 }}>{v}</Text>,
    },
    { title: 'Enviado?', dataIndex: 'sent_to_support', width: 90, align: 'center',
      render: (v) => v ? <Tag color="green">✓</Tag> : <Tag color="default">—</Tag>,
    },
    { title: 'Tokens', width: 90, align: 'right',
      render: (_, r) => r.input_tokens
        ? <Tooltip title={`in ${r.input_tokens} / out ${r.output_tokens}`}>
            <Text style={{ fontSize: 11 }}>{(r.input_tokens || 0) + (r.output_tokens || 0)}</Text>
          </Tooltip>
        : '—',
    },
    { title: '', width: 60,
      render: (_, r) => <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(r)} />,
    },
  ];

  const isOn = settings?.enabled;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <RobotOutlined /> AI Suporte
          </Title>
          <Text type="secondary">
            IA analisa msgs de organizadores no Chatwoot e sugere ação no grupo de suporte.
          </Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <Card size="small">
            <Space size="large" align="center">
              <PoweroffOutlined style={{ fontSize: 32, color: isOn ? '#52c41a' : '#999' }} />
              <Space direction="vertical" size={0}>
                <Text strong style={{ fontSize: 16 }}>Análise automática</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isOn ? 'Ativa — analisando msgs em tempo real' : 'DESLIGADA — não gasta tokens'}
                </Text>
              </Space>
              <Switch checked={!!isOn} onChange={toggleEnabled} loading={saving} checkedChildren="ON" unCheckedChildren="OFF" />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={14}>
          <Card size="small">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>Inboxes permitidas (IDs Chatwoot, separados por vírgula)</Text>
              <Space.Compact style={{ width: '100%' }}>
                <Input value={inboxesInput} onChange={(e) => setInboxesInput(e.target.value)} placeholder="67,1" />
                <Button type="primary" onClick={saveInboxes} loading={saving}>Salvar</Button>
              </Space.Compact>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Atual: <code>{(settings?.allowed_inbox_ids || []).join(', ') || '(nenhuma)'}</code>
                {' — '}
                Vazio = todas. Conhecidas: 67=Futezap-Organizer, 1=TIM.
              </Text>
              {settings?.updated_at && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Última mudança: {dayjs(settings.updated_at).format('DD/MM HH:mm')} por {settings.updated_by || '—'}
                </Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {!isOn && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="AI suporte desligada"
          description="Enquanto desligado, msgs entrantes no Chatwoot não são analisadas nem geram custo de token."
        />
      )}

      <Card title={`Últimas análises (${suggestions.length})`} size="small">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={suggestions}
          columns={columns}
          pagination={{ pageSize: 25 }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        title={detail ? `Análise #${detail.id} — ${dayjs(detail.created_at).format('DD/MM HH:mm:ss')}` : ''}
        width={800}
        footer={<Button onClick={() => setDetail(null)}>Fechar</Button>}
      >
        {detail && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Time:</Text>
              <br />
              {detail.customer_id
                ? <a href={`/customers/${detail.customer_id}/ficha`} target="_blank" rel="noreferrer">#{detail.customer_id}</a>
                : <Text type="secondary">não identificado — tel {detail.organizer_phone}</Text>}
              {' • Severidade: '}
              <Tag color={SEV_COLORS[detail.severity] || 'default'}>{detail.severity}</Tag>
              {' • Enviado ao grupo: '}
              {detail.sent_to_support ? <Tag color="green">SIM</Tag> : <Tag>NÃO</Tag>}
            </div>
            <div>
              <Text type="secondary">Msg do organizador:</Text>
              <Paragraph style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginTop: 4 }}>{detail.organizer_message}</Paragraph>
            </div>
            <div>
              <Text type="secondary">Sugestão da IA:</Text>
              <Paragraph style={{ background: '#e6f4ff', padding: 12, borderRadius: 4, marginTop: 4, whiteSpace: 'pre-wrap' }}>{detail.suggestion}</Paragraph>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Tokens: in {detail.input_tokens || 0} / out {detail.output_tokens || 0}
                {detail.chatwoot_conversation_id && (
                  <> • <a href={`https://atendimento.mooblab.com/app/accounts/1/conversations/${detail.chatwoot_conversation_id}`} target="_blank" rel="noreferrer">Ver conversa Chatwoot</a></>
                )}
              </Text>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AiSupport;
